import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@h2/ui';
import { Input } from '@h2/ui';
import { ChevronDown } from 'lucide-react';
import { useWorkerStore } from '../../../stores/useWorkerStore';
import { useWorkerGroupStore } from '../../../stores/useWorkerGroupStore';
import { usePricePresetStore } from '../../../stores/usePricePresetStore';
import Icon from '../../ui/icon';
import {
  useFloating,
  autoUpdate,
  flip,
  shift,
  offset,
  size,
  arrow,
  FloatingPortal,
  FloatingArrow,
} from '@floating-ui/react';

interface AssignmentCellProps {
  cell: {
    languageCode: string;
    taskType: string;
    assignee: {
      type: 'worker' | 'group' | 'ai' | 'pm' | 'auto' | null;
      id: string | null;
      name: string | null;
    };
    unitPrice: number | null;
    currency: string;
    splitUnit?: 'none' | '5min' | '10min' | '15min';
    priceAdjustment?: number;
  };
  onUpdate: (updates: any) => void;
}

export const AssignmentCell: React.FC<AssignmentCellProps> = ({
  cell,
  onUpdate
}) => {
  const { workers } = useWorkerStore();
  const { groups } = useWorkerGroupStore();
  const { loadPresets, searchPresets } = usePricePresetStore();
  const [assigneeType, setAssigneeType] = useState<'worker' | 'group' | 'ai' | 'pm' | 'auto'>(
    cell.assignee?.type || 'group' // 디폴트를 그룹으로 설정
  );
  const [searchTerm, setSearchTerm] = useState(cell.assignee?.name || '');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<any[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  // Floating UI for assignee dropdown
  const {
    refs: assigneeRefs,
    floatingStyles: assigneeFloatingStyles,
    context: assigneeContext
  } = useFloating({
    open: isDropdownOpen,
    onOpenChange: setIsDropdownOpen,
    middleware: [
      offset(5),
      flip(),
      shift({ padding: 5 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: '200px',
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });
  
  // 단가 관련 상태
  const [priceSearchTerm, setPriceSearchTerm] = useState('');
  const [selectedPresetName, setSelectedPresetName] = useState('');
  const [isPriceDropdownOpen, setIsPriceDropdownOpen] = useState(false);
  const [filteredPriceOptions, setFilteredPriceOptions] = useState<any[]>([]);
  const [selectedPriceIndex, setSelectedPriceIndex] = useState(-1);
  const priceInputRef = useRef<HTMLInputElement>(null);
  // Floating UI for price dropdown
  const {
    refs: priceRefs,
    floatingStyles: priceFloatingStyles,
    context: priceContext
  } = useFloating({
    open: isPriceDropdownOpen,
    onOpenChange: setIsPriceDropdownOpen,
    middleware: [
      offset(5),
      flip(),
      shift({ padding: 5 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: '200px',
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });
  
  // 단가 조정 관련 상태
  const [isPriceAdjustmentOpen, setIsPriceAdjustmentOpen] = useState(false);
  const [customAdjustment, setCustomAdjustment] = useState('');
  const priceAdjustmentRef = useRef<HTMLDivElement>(null);
  const adjustmentButtonRef = useRef<HTMLButtonElement>(null);

  // 타입 선택 드롭다운 관련 상태
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const typeDropdownRef = useRef<HTMLDivElement>(null);

  // Floating UI for type dropdown
  const {
    refs: typeRefs,
    floatingStyles: typeFloatingStyles,
    context: typeContext
  } = useFloating({
    open: isTypeDropdownOpen,
    onOpenChange: setIsTypeDropdownOpen,
    placement: 'bottom-start',
    middleware: [
      offset(5),
      flip(),
      shift({ padding: 5 }),
      size({
        apply({ elements }) {
          Object.assign(elements.floating.style, {
            minWidth: '180px',
            maxHeight: '200px',
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  // Floating UI for price adjustment dropdown
  const {
    refs: adjustmentRefs,
    floatingStyles: adjustmentFloatingStyles,
    context: adjustmentContext
  } = useFloating({
    open: isPriceAdjustmentOpen,
    onOpenChange: setIsPriceAdjustmentOpen,
    placement: 'bottom-end',
    middleware: [
      offset(5),
      flip(),
      shift({ padding: 5 }),
      size({
        apply({ elements }) {
          Object.assign(elements.floating.style, {
            minWidth: '180px',
            maxHeight: '300px',
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });
  
  // 분할단위 상태
  const [splitUnit, setSplitUnit] = useState<'none' | '5min' | '10min' | '15min'>(
    cell.splitUnit || 'none'
  );
  
  // 드롭다운 위치 상태
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [priceDropdownPosition, setPriceDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  const currencySymbols: Record<string, string> = {
    'KRW': '₩',
    'USD': '$',
    'JPY': '¥',
    'CNY': '¥',
    'EUR': '€',
    'INR': '₹'
  };

  // 프리셋 로드
  useEffect(() => {
    loadPresets();
  }, [loadPresets]);

  // 검색어에 따른 옵션 필터링 (Worker/Group만)
  useEffect(() => {
    if (assigneeType === 'ai' || assigneeType === 'pm' || assigneeType === 'auto') {
      setFilteredOptions([]);
      return;
    }
    
    const options = assigneeType === 'worker' 
      ? workers.filter(w => w.status === 'active')
      : groups.filter(g => g.status === 'active');
    
    const filtered = options.filter(option =>
      option.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setFilteredOptions(filtered);
    setSelectedIndex(-1); // 옵션이 변경되면 선택 초기화
  }, [assigneeType, searchTerm, workers, groups]);

  // 단가 프리셋 필터링
  useEffect(() => {
    let filtered = searchPresets(priceSearchTerm);
    
    // 숫자로 검색 시 금액으로도 필터링
    if (priceSearchTerm && !isNaN(Number(priceSearchTerm))) {
      const numSearch = Number(priceSearchTerm);
      const allPresets = searchPresets(''); // 모든 프리셋 가져오기
      const amountFiltered = allPresets.filter(preset => 
        preset.amount.toString().includes(priceSearchTerm) ||
        preset.amount === numSearch
      );
      
      // 기존 텍스트 검색 결과와 합치기 (중복 제거)
      const combinedResults = [...filtered];
      amountFiltered.forEach(preset => {
        if (!combinedResults.find(p => p.id === preset.id)) {
          combinedResults.push(preset);
        }
      });
      filtered = combinedResults;
    }
    
    setFilteredPriceOptions(filtered);
    setSelectedPriceIndex(-1); // 옵션이 변경되면 선택 초기화
  }, [priceSearchTerm, searchPresets]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // 작업자 드롭다운
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      
      // 단가 드롭다운
      if (priceDropdownRef.current && !priceDropdownRef.current.contains(event.target as Node) &&
          priceInputRef.current && !priceInputRef.current.contains(event.target as Node)) {
        setIsPriceDropdownOpen(false);
      }
      
      // 단가 조정 드롭다운
      if (priceAdjustmentRef.current && !priceAdjustmentRef.current.contains(event.target as Node)) {
        setIsPriceAdjustmentOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTypeSelect = (newType: 'worker' | 'group' | 'ai' | 'pm' | 'auto') => {
    setAssigneeType(newType);
    setSearchTerm('');
    
    // AI/PM/Auto일 때는 자동으로 설정
    if (newType === 'ai') {
      onUpdate({ 
        assignee: { type: 'ai', id: 'ai-auto', name: 'AI' }
      });
    } else if (newType === 'pm') {
      onUpdate({ 
        assignee: { type: 'pm', id: 'pm-auto', name: 'PM' }
      });
    } else if (newType === 'auto') {
      onUpdate({ 
        assignee: { type: 'auto', id: 'auto-assign', name: '자동배정' }
      });
    } else {
      onUpdate({ assignee: null }); // worker/group일 때만 선택 초기화
    }
    
    setIsDropdownOpen(false);
    setIsTypeDropdownOpen(false);
  };

  // 타입 드롭다운 blur 핸들러
  const handleTypeDropdownBlur = () => {
    setTimeout(() => {
      setIsTypeDropdownOpen(false);
    }, 150);
  };

  // 타입 드롭다운 키보드 핸들러
  const handleTypeDropdownKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' || e.key === 'Tab') {
      setIsTypeDropdownOpen(false);
    }
  };

  const getTypeInfo = (type: 'worker' | 'group' | 'ai' | 'pm' | 'auto') => {
    switch (type) {
      case 'worker':
        return { label: '작업자', icon: 'User', color: 'text-gray-600' };
      case 'group':
        return { label: '작업자 그룹', icon: 'Users', color: 'text-gray-600' };
      case 'ai':
        return { label: 'AI', icon: 'Zap', color: 'text-green-600' };
      case 'pm':
        return { label: 'PM', icon: 'Shield', color: 'text-blue-600' };
      case 'auto':
        return { label: '자동배정', icon: 'Settings', color: 'text-pink-600' };
      default:
        return { label: '작업자 그룹', icon: 'Users', color: 'text-gray-600' };
    }
  };

  const handleOptionSelect = (option: any) => {
    const assignee = {
      type: assigneeType,
      id: option.id,
      name: option.name
    };
    onUpdate({ assignee });
    setSearchTerm(option.name);
    setIsDropdownOpen(false);
  };


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsDropdownOpen(true);
    
    // 입력값이 변경되면 기존 선택 해제
    if (cell.assignee && value !== cell.assignee.name) {
      onUpdate({ assignee: null });
    }
  };

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        setSelectedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        const indexToSelect = selectedIndex >= 0 ? selectedIndex : 0;
        handleOptionSelect(filteredOptions[indexToSelect]);
      }
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      setIsDropdownOpen(false);
      setSelectedIndex(-1);
    }
  };

  const handleInputBlur = () => {
    // 약간의 지연을 두어 클릭 이벤트가 먼저 처리되도록 함
    setTimeout(() => {
      setIsDropdownOpen(false);
      setSelectedIndex(-1);
    }, 150);
  };
  
  const handlePriceChange = (price: number) => {
    onUpdate({ unitPrice: price });
  };

  // 단가 관련 핸들러들
  const handlePriceInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPriceSearchTerm(value);
    setSelectedPresetName(''); // 직접 입력 시 프리셋 이름 초기화
    setIsPriceDropdownOpen(true);
    
    // 숫자만 입력된 경우 바로 단가 업데이트
    if (value.trim() === '') {
      onUpdate({ unitPrice: null }); // 빈 값일 때 null로 설정
    } else {
      const numValue = Number(value);
      if (!isNaN(numValue) && numValue >= 0) {
        handlePriceChange(numValue);
      } else if (value.trim() !== '' && isNaN(Number(value))) {
        // 숫자가 아닌 값이 입력되었지만 검색어로 사용 중일 수 있으므로 unitPrice는 null로 유지
        onUpdate({ unitPrice: null });
      }
    }
  };

  const handlePriceInputFocus = () => {
    setIsPriceDropdownOpen(true);
    
    // 프리셋이 선택되어 있다면 검색어로 변환
    if (selectedPresetName && !priceSearchTerm) {
      setPriceSearchTerm('');
    }
  };

  const handlePriceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filteredPriceOptions.length > 0) {
        setSelectedPriceIndex(prev => 
          prev < filteredPriceOptions.length - 1 ? prev + 1 : 0
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filteredPriceOptions.length > 0) {
        setSelectedPriceIndex(prev => 
          prev > 0 ? prev - 1 : filteredPriceOptions.length - 1
        );
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      
      // 검색 결과가 있으면 선택된 프리셋 선택
      if (filteredPriceOptions.length > 0) {
        const indexToSelect = selectedPriceIndex >= 0 ? selectedPriceIndex : 0;
        handlePricePresetSelect(filteredPriceOptions[indexToSelect]);
      } 
      // 검색 결과가 없고 숫자가 입력되었으면 직접 입력으로 처리
      else if (priceSearchTerm && !isNaN(Number(priceSearchTerm))) {
        const numValue = Number(priceSearchTerm);
        onUpdate({ unitPrice: numValue });
        setSelectedPresetName(''); // 직접 입력이므로 프리셋 이름 없음
        setPriceSearchTerm(''); // 검색어 초기화
        setIsPriceDropdownOpen(false);
      }
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      setIsPriceDropdownOpen(false);
      setSelectedPriceIndex(-1);
    }
  };

  const handlePriceInputBlur = () => {
    // 약간의 지연을 두어 클릭 이벤트가 먼저 처리되도록 함
    setTimeout(() => {
      setIsPriceDropdownOpen(false);
      setSelectedPriceIndex(-1);
    }, 150);
  };

  const handlePricePresetSelect = (preset: any) => {
    const amount = preset?.amount || null;
    const currency = preset?.currency || 'KRW';
    
    onUpdate({ 
      unitPrice: amount, 
      currency: currency 
    });
    setSelectedPresetName(preset?.name || '');
    setPriceSearchTerm(''); // 검색어 초기화
    setIsPriceDropdownOpen(false);
  };

  const handleSplitUnitChange = (unit: 'none' | '5min' | '10min' | '15min') => {
    setSplitUnit(unit);
    onUpdate({ splitUnit: unit });
  };

  // 단가 조정 핸들러들
  const handlePriceAdjustment = (adjustment: number) => {
    onUpdate({ priceAdjustment: adjustment });
    setIsPriceAdjustmentOpen(false);
  };

  const handleCustomAdjustment = () => {
    const adjustment = Number(customAdjustment);
    if (!isNaN(adjustment) && adjustment !== 0) {
      handlePriceAdjustment(adjustment);
      setCustomAdjustment('');
    }
  };

  const handleResetAdjustment = () => {
    onUpdate({ priceAdjustment: 0 });
    setIsPriceAdjustmentOpen(false);
  };

  const getFinalPrice = () => {
    const basePrice = (cell.unitPrice !== null && cell.unitPrice !== undefined) ? cell.unitPrice : 0;
    const adjustment = cell.priceAdjustment || 0;
    return basePrice + adjustment;
  };

  const isAdjustmentExceedsLimit = (adjustment: number) => {
    const basePrice = cell.unitPrice || 0;
    if (basePrice === 0) return false;
    const percentage = Math.abs(adjustment) / basePrice;
    return percentage > 0.3; // 30% 초과
  };

  const getAdjustmentWarning = (inputValue: string) => {
    const adjustment = Number(inputValue);
    if (isNaN(adjustment) || adjustment === 0) return null;
    
    if (isAdjustmentExceedsLimit(adjustment)) {
      const basePrice = cell.unitPrice || 0;
      const percentage = Math.abs(adjustment) / basePrice * 100;
      return `경고: 기준 단가의 ${percentage.toFixed(1)}%를 초과합니다 (30% 초과)`;
    }
    return null;
  };
  
  return (
    <div className="space-y-2 w-full">
      {/* 작업자/그룹 선택 */}
      <div className="relative">
               {/* 타입 선택 드롭다운 */}
               <div className="mb-1">
                 <div className="relative">
                   <Button
                     ref={(el) => {
                       typeRefs.setReference(el);
                     }}
                     type="button"
                     variant="outline"
                     size="sm"
                     onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
                     onBlur={handleTypeDropdownBlur}
                     onKeyDown={handleTypeDropdownKeyDown}
                     className={`w-full h-7 px-2 text-xs justify-between ${getTypeInfo(assigneeType).color} border-gray-300`}
                   >
                     <div className="flex items-center gap-1">
                       <Icon name={getTypeInfo(assigneeType).icon} size={12} />
                       <span>{getTypeInfo(assigneeType).label}</span>
                     </div>
                     <ChevronDown size={12} />
                   </Button>
                 </div>
               </div>
        
        {/* 검색 입력 - AI/PM일 때만 표시 */}
        {(assigneeType === 'worker' || assigneeType === 'group') && (
          <div className="relative">
                 <Input
                   ref={(el) => {
                     inputRef.current = el;
                     assigneeRefs.setReference(el);
                   }}
                   type="text"
                   placeholder={`${getTypeInfo(assigneeType).label} 검색...`}
                   value={searchTerm}
                   onChange={handleInputChange}
                   onKeyDown={handleKeyDown}
                   onFocus={handleInputFocus}
                   onBlur={handleInputBlur}
                   className="h-8 text-xs pr-8"
                 />
            <ChevronDown 
              className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" 
            />
          </div>
        )}

        {/* AI/PM/자동배정 자동 설정 표시 */}
        {(assigneeType === 'ai' || assigneeType === 'pm' || assigneeType === 'auto') && (
          <div className={`p-3 rounded-md border-2 border-dashed ${
            assigneeType === 'ai' 
              ? 'border-green-300 bg-green-50' 
              : assigneeType === 'pm'
                ? 'border-blue-300 bg-blue-50'
                : 'border-pink-300 bg-pink-50'
          }`}>
            <div className="flex items-center justify-center gap-2">
              <Icon 
                name={getTypeInfo(assigneeType).icon} 
                size={16} 
                className={getTypeInfo(assigneeType).color}
              />
              <span className={`text-sm font-medium ${getTypeInfo(assigneeType).color}`}>
                {assigneeType === 'auto' ? '작업자 자동 배정' : `${getTypeInfo(assigneeType).label} 자동 배정`}
              </span>
            </div>
            <div className="text-xs text-center mt-1 text-gray-500">
              별도 선택 불필요
            </div>
          </div>
        )}
        
        
        {/* 분할단위 (작업자 그룹일 때만 표시) */}
        {assigneeType === 'group' && (
          <div className="mt-3 relative z-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">분할단위</span>
            </div>
            <div className="flex gap-2">
              {[
                { value: 'none', label: '분할없음' },
                { value: '5min', label: '5분' },
                { value: '10min', label: '10분' },
                { value: '15min', label: '15분' }
              ].map((option) => (
                <label 
                  key={option.value}
                  className="flex items-center gap-1 cursor-pointer"
                >
                  <input
                    type="radio"
                    name={`split-unit-${cell.languageCode}-${cell.taskType}`}
                    value={option.value}
                    checked={splitUnit === option.value}
                    onChange={() => handleSplitUnitChange(option.value as any)}
                    className="w-3 h-3"
                  />
                  <span className="text-xs">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* 단가 입력 - AI/PM일 때 숨김 */}
      {(assigneeType === 'worker' || assigneeType === 'group') && (
      <div className="relative">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground">단가</span>
          <div className="relative">
            <Button
              ref={(el) => {
                adjustmentButtonRef.current = el;
                adjustmentRefs.setReference(el);
              }}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsPriceAdjustmentOpen(!isPriceAdjustmentOpen)}
              className={`h-5 px-1 text-xs ${
                cell.priceAdjustment && cell.priceAdjustment !== 0 
                  ? cell.priceAdjustment > 0 
                    ? 'text-blue-600 hover:text-blue-700' 
                    : 'text-red-600 hover:text-red-700'
                  : cell.unitPrice 
                    ? 'text-muted-foreground hover:text-foreground' 
                    : 'text-muted-foreground opacity-50 cursor-not-allowed'
              }`}
              disabled={!cell.unitPrice}
            >
              단가조정
            </Button>
          </div>
        </div>
        
        <div className="relative">
          <Input
            ref={(el) => {
              priceInputRef.current = el;
              priceRefs.setReference(el);
            }}
            type="text"
            placeholder="단가 검색 또는 직접 입력..."
            value={
              priceSearchTerm !== '' 
                ? priceSearchTerm
                : selectedPresetName 
                  ? `${selectedPresetName} (기본단가 ${currencySymbols[cell.currency] || cell.currency}${(cell.unitPrice || 0).toLocaleString()})`
                  : (cell.unitPrice !== null && cell.unitPrice !== undefined)
                    ? `기본단가 ${currencySymbols[cell.currency] || cell.currency}${cell.unitPrice.toLocaleString()}`
                    : ''
            }
            onChange={handlePriceInputChange}
            onKeyDown={handlePriceKeyDown}
            onFocus={handlePriceInputFocus}
            onBlur={handlePriceInputBlur}
            className="h-8 text-xs pr-8"
          />
          <ChevronDown 
            className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" 
          />
          
          {/* 조정 금액 표시 */}
          {cell.priceAdjustment && cell.priceAdjustment !== 0 && (
            <span className={`absolute right-8 top-1/2 transform -translate-y-1/2 text-xs font-medium ${
              cell.priceAdjustment > 0 ? 'text-blue-600' : 'text-red-600'
            }`}>
              {cell.priceAdjustment > 0 ? '+' : ''}{cell.priceAdjustment.toLocaleString()}
            </span>
          )}
          
          {/* 통화 표시 (조정이 없고 프리셋이 선택되지 않았을 때만) */}
          {(cell.unitPrice !== null && cell.unitPrice !== undefined) && !selectedPresetName && (!cell.priceAdjustment || cell.priceAdjustment === 0) && (
            <span className="absolute right-8 top-1/2 transform -translate-y-1/2 text-xs text-muted-foreground">
              {currencySymbols[cell.currency] || cell.currency || 'KRW'}
            </span>
          )}
        </div>
        
      </div>
      )}
      
      {/* Floating UI Dropdowns - Worker/Group만 */}
      {isDropdownOpen && (assigneeType === 'worker' || assigneeType === 'group') && (
        <FloatingPortal>
          <div
            ref={assigneeRefs.setFloating}
            style={assigneeFloatingStyles}
            className="bg-white border rounded-md shadow-lg z-[2000] overflow-y-auto"
          >
            {filteredOptions.length === 0 ? (
              <div className="p-2 text-xs text-muted-foreground text-center">
                {searchTerm ? '검색 결과가 없습니다.' : `${assigneeType === 'worker' ? '작업자' : '작업자 그룹'}가 없습니다.`}
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <button
                  key={option.id}
                  className={`w-full text-left p-2 text-xs flex items-center gap-2 ${
                    index === selectedIndex 
                      ? 'bg-blue-50 border-l-2 border-blue-500' 
                      : 'hover:bg-accent'
                  } ${getTypeInfo(assigneeType).color}`}
                  onClick={() => handleOptionSelect(option)}
                >
                  <Icon name={getTypeInfo(assigneeType).icon} size={12} />
                  <span>{option.name}</span>
                </button>
              ))
            )}
          </div>
        </FloatingPortal>
      )}

      {isPriceDropdownOpen && (
        <FloatingPortal>
          <div
            ref={priceRefs.setFloating}
            style={priceFloatingStyles}
            className="bg-white border rounded-md shadow-lg z-[2000] overflow-y-auto"
          >
            {filteredPriceOptions.length === 0 ? (
              <div className="p-2 text-xs text-muted-foreground text-center">
                {priceSearchTerm ? '검색 결과가 없습니다.' : '단가 프리셋이 없습니다.'}
              </div>
            ) : (
              filteredPriceOptions.map((preset, index) => (
                <button
                  key={preset.id}
                  className={`w-full text-left p-2 text-xs ${
                    index === selectedPriceIndex 
                      ? 'bg-blue-50 border-l-2 border-blue-500' 
                      : 'hover:bg-accent'
                  }`}
                  onClick={() => handlePricePresetSelect(preset)}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{preset.name}</span>
                    <span className="text-muted-foreground">
                      {currencySymbols[preset.currency]}{preset.amount.toLocaleString()}
                    </span>
                  </div>
                  {preset.description && (
                    <div className="text-muted-foreground text-xs mt-1">
                      {preset.description}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </FloatingPortal>
      )}

      {isPriceAdjustmentOpen && (
        <FloatingPortal>
          <div
            ref={adjustmentRefs.setFloating}
            style={adjustmentFloatingStyles}
            className="bg-white border rounded-md shadow-lg z-[2000]"
          >
            <div className="p-2 space-y-1">
              <button
                className="w-full text-left p-1 hover:bg-accent text-xs text-blue-600"
                onClick={() => handlePriceAdjustment(500)}
              >
                +500
              </button>
              <button
                className="w-full text-left p-1 hover:bg-accent text-xs text-blue-600"
                onClick={() => handlePriceAdjustment(1000)}
              >
                +1000
              </button>
              <button
                className="w-full text-left p-1 hover:bg-accent text-xs text-red-600"
                onClick={() => handlePriceAdjustment(-500)}
              >
                -500
              </button>
              <button
                className="w-full text-left p-1 hover:bg-accent text-xs text-red-600"
                onClick={() => handlePriceAdjustment(-1000)}
              >
                -1000
              </button>
              
              <div className="border-t pt-1 mt-1">
                <div className="flex gap-1">
                  <Input
                    type="number"
                    placeholder="수동입력"
                    value={customAdjustment}
                    onChange={(e) => setCustomAdjustment(e.target.value)}
                    className={`h-6 text-xs flex-1 ${
                      customAdjustment && isAdjustmentExceedsLimit(Number(customAdjustment))
                        ? 'border-red-500 focus:border-red-500'
                        : ''
                    }`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleCustomAdjustment();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCustomAdjustment}
                    className="h-6 px-2 text-xs"
                  >
                    적용
                  </Button>
                </div>
                {customAdjustment && getAdjustmentWarning(customAdjustment) && (
                  <div className="text-xs text-red-600 mt-1 p-1 bg-red-50 rounded">
                    {getAdjustmentWarning(customAdjustment)}
                  </div>
                )}
              </div>
              
              {cell.priceAdjustment && cell.priceAdjustment !== 0 && (
                <div className="border-t pt-1 mt-1">
                  <button
                    className="w-full text-left p-1 hover:bg-accent text-xs text-gray-600"
                    onClick={handleResetAdjustment}
                  >
                    조정해제
                  </button>
                </div>
              )}
            </div>
          </div>
        </FloatingPortal>
      )}

      {/* 타입 선택 드롭다운 */}
      {isTypeDropdownOpen && (
        <FloatingPortal>
          <div
            ref={typeRefs.setFloating}
            style={typeFloatingStyles}
            className="bg-white border rounded-md shadow-lg z-[2000] overflow-y-auto"
          >
            {[
              { type: 'group' as const, info: getTypeInfo('group') },
              { type: 'worker' as const, info: getTypeInfo('worker') },
              { type: 'ai' as const, info: getTypeInfo('ai') },
              { type: 'pm' as const, info: getTypeInfo('pm') },
              { type: 'auto' as const, info: getTypeInfo('auto') }
            ].map(({ type, info }) => (
              <button
                key={type}
                className={`w-full text-left p-2 text-xs flex items-center gap-2 hover:bg-accent ${
                  type === assigneeType ? 'bg-blue-50 border-l-2 border-blue-500' : ''
                } ${info.color}`}
                onClick={() => handleTypeSelect(type)}
              >
                <Icon name={info.icon} size={14} />
                <span className="font-medium">{info.label}</span>
              </button>
            ))}
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};
